import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  DatePicker,
  Select,
  Button,
  Tabs,
  Table,
  Progress,
  Space,
} from 'antd';
import {
  TeamOutlined,
  ShopOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  usePlatformAnalytics,
  useTenantGrowth,
  useRevenueTrend,
  useUserGrowth,
  useRealtimeMetrics,
  useApiUsage,
} from '../../hooks';
import { MetricCard, LineChart } from '../../components';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

export const PlatformAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [activeTab, setActiveTab] = useState('overview');

  const dateParams = {
    startDate: dateRange[0].format('YYYY-MM-DD'),
    endDate: dateRange[1].format('YYYY-MM-DD'),
    period,
  };

  const { analytics, loading: analyticsLoading } = usePlatformAnalytics(dateParams);
  const { data: tenantGrowth, loading: growthLoading } = useTenantGrowth(dateParams);
  const { data: revenueTrend, loading: revenueLoading } = useRevenueTrend(dateParams);
  const { data: userGrowth, loading: userLoading } = useUserGrowth(dateParams);
  const { metrics: realtimeMetrics, loading: realtimeLoading } = useRealtimeMetrics();
  const { data: apiUsage, loading: apiLoading } = useApiUsage(dateParams);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCompactNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Platform Analytics</Title>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates) {
                  setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                }
              }}
            />
            <Select value={period} onChange={setPeriod} style={{ width: 100 }}>
              <Option value="day">Day</Option>
              <Option value="week">Week</Option>
              <Option value="month">Month</Option>
            </Select>
            <Button type="primary">Refresh</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <MetricCard
            title="Total Tenants"
            value={analytics?.metrics?.totalTenants || 0}
            icon={<ShopOutlined />}
            color="#1890ff"
            loading={analyticsLoading}
          />
        </Col>
        <Col span={4}>
          <MetricCard
            title="Active Tenants"
            value={analytics?.metrics?.activeTenants || 0}
            icon={<CheckCircleIcon />}
            color="#52c41a"
            loading={analyticsLoading}
          />
        </Col>
        <Col span={4}>
          <MetricCard
            title="Total Users"
            value={analytics?.metrics?.totalUsers || 0}
            icon={<UserOutlined />}
            color="#722ed1"
            loading={analyticsLoading}
          />
        </Col>
        <Col span={4}>
          <MetricCard
            title="Active Users"
            value={realtimeMetrics?.activeUsers || 0}
            icon={<TeamOutlined />}
            color="#fa8c16"
            loading={realtimeLoading}
          />
        </Col>
        <Col span={4}>
          <MetricCard
            title="Total Revenue"
            value={analytics?.metrics?.totalRevenue || 0}
            prefix="₫"
            icon={<DollarOutlined />}
            color="#13c2c2"
            loading={analyticsLoading}
          />
        </Col>
        <Col span={4}>
          <MetricCard
            title="Total Orders"
            value={analytics?.metrics?.totalOrders || 0}
            icon={<ShoppingCartOutlined />}
            color="#eb2f96"
            loading={analyticsLoading}
          />
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
        <TabPane tab="Overview" key="overview">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <LineChart
                title="Tenant Growth"
                data={tenantGrowth.map((item) => ({
                  date: item.period,
                  value: item.totalTenants,
                  category: 'Total',
                }))}
                loading={growthLoading}
                seriesField="category"
              />
            </Col>
            <Col span={12}>
              <LineChart
                title="Revenue Trend"
                data={revenueTrend}
                loading={revenueLoading}
                yAxis={{
                  formatter: (v: number) => `₫${(v / 1000000).toFixed(0)}M`,
                }}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <LineChart
                title="User Growth"
                data={userGrowth}
                loading={userLoading}
              />
            </Col>
            <Col span={12}>
              <LineChart
                title="API Requests"
                data={apiUsage}
                loading={apiLoading}
              />
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Top Tenants" key="top-tenants">
          <Card title="Top Tenants by Revenue">
            <Table
              columns={[
                { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 80, render: (_, __, index) => index + 1 },
                { title: 'Tenant', dataIndex: 'name', key: 'name' },
                {
                  title: 'Revenue',
                  dataIndex: 'revenue',
                  key: 'revenue',
                  align: 'right',
                  render: (v) => formatCurrency(v),
                },
                {
                  title: 'Orders',
                  dataIndex: 'orders',
                  key: 'orders',
                  align: 'right',
                  render: (v) => formatCompactNumber(v),
                },
                {
                  title: 'Users',
                  dataIndex: 'users',
                  key: 'users',
                  align: 'right',
                },
                {
                  title: 'Share',
                  key: 'share',
                  render: (_, record) => {
                    const total = analytics?.topTenants?.reduce((sum, t) => sum + t.revenue, 0) || 1;
                    const percentage = ((record.revenue / total) * 100).toFixed(1);
                    return (
                      <Space>
                        <Progress percent={Number(percentage)} size="small" style={{ width: 100 }} />
                        <Text>{percentage}%</Text>
                      </Space>
                    );
                  },
                },
              ]}
              dataSource={analytics?.topTenants || []}
              loading={analyticsLoading}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </TabPane>

        <TabPane tab="Revenue by Plan" key="revenue-by-plan">
          <Row gutter={[16, 16]}>
            {analytics?.revenueByPlan?.map((item) => (
              <Col span={6} key={item.plan}>
                <Card>
                  <Statistic
                    title={item.plan.toUpperCase()}
                    value={item.revenue}
                    prefix="₫"
                    formatter={(value) => formatCurrency(value as number)}
                  />
                  <Progress
                    percent={item.percentage}
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                    style={{ marginTop: 16 }}
                  />
                  <Text type="secondary">
                    {item.percentage}% of total revenue
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>

        <TabPane tab="Realtime" key="realtime">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Active Users Now"
                  value={realtimeMetrics?.activeUsers || 0}
                  prefix={<UserOutlined />}
                  loading={realtimeLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="API Requests/min"
                  value={realtimeMetrics?.apiRequestsPerMinute || 0}
                  prefix={<ApiOutlined />}
                  loading={realtimeLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Orders Today"
                  value={realtimeMetrics?.ordersToday || 0}
                  prefix={<ShoppingCartOutlined />}
                  loading={realtimeLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Revenue Today"
                  value={realtimeMetrics?.revenueToday || 0}
                  prefix="₫"
                  loading={realtimeLoading}
                  formatter={(value) => formatCurrency(value as number)}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

// Simple icon component
const CheckCircleIcon = () => <RiseOutlined />;

export default PlatformAnalytics;
